import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import * as actionsNotificacao from 'store/notificacao/actions';
import * as selectorsNotificacao from 'store/notificacao/reducer';
import * as actionsRelatorioJob from 'store/relatorioJob/actions';
import * as selectorsRelatorioJob from 'store/relatorioJob/reducer';
import {
  NOTIFICACAO_POLL_MS,
  canNavigateNotificacao,
  getRelatorioJobFromNotificacao,
  isNotificacaoLida,
  isRelatorioGerando,
} from 'helpers/notificacao';
import NotificacaoItem from 'views/v2/Notificacoes/NotificacaoItem';

const PREVIEW_REFRESH_MS = 5000;
const POPOVER_ID = 'notificacao-bell-popover';
const PANEL_MAX_HEIGHT = 560;
const PANEL_VIEWPORT_GAP = 96;
const PANEL_CHROME = 128;
const previewPanelMaxHeight = `min(${PANEL_MAX_HEIGHT}px, calc(100dvh - ${PANEL_VIEWPORT_GAP}px))`;
const previewListMaxHeight = `min(${PANEL_MAX_HEIGHT - PANEL_CHROME}px, calc(100dvh - ${PANEL_VIEWPORT_GAP + PANEL_CHROME}px))`;

const NotificacaoBell = ({
  unreadCount,
  preview,
  downloadingId,
  fetchUnreadCount,
  fetchNotificacaoPreview,
  markNotificacaoRead,
  markAllNotificacoesRead,
  downloadRelatorioJob,
  history,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const empty = (preview || []).length === 0;
  const hasGerando = (preview || []).some((item) => isRelatorioGerando(item));

  useEffect(() => {
    fetchUnreadCount();
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(fetchUnreadCount, NOTIFICACAO_POLL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!open || !hasGerando) return undefined;
    const timer = window.setInterval(fetchNotificacaoPreview, PREVIEW_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [open, hasGerando, fetchNotificacaoPreview]);

  const handleClose = () => setAnchorEl(null);

  const handleOpenItem = (item) => {
    handleClose();
    if (!isNotificacaoLida(item)) markNotificacaoRead(item.id, true);
    if (canNavigateNotificacao(item)) {
      history.push(item.referenciaPath);
    }
  };

  const handleDownload = (item, job) => {
    downloadRelatorioJob(job);
    if (!isNotificacaoLida(item)) markNotificacaoRead(item.id, true);
  };

  return (
    <>
      <Tooltip title="Notificações">
        <IconButton
          aria-label="Notificações"
          aria-controls={open ? POPOVER_ID : undefined}
          aria-haspopup="true"
          aria-expanded={open}
          color="inherit"
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
            fetchNotificacaoPreview();
          }}
          sx={{
            color: (theme) => (
              theme.palette.mode === 'dark'
                ? theme.palette.common.white
                : theme.palette.text.primary
            ),
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            max={99}
          >
            <NotificationsOutlinedIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        id={POPOVER_ID}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 420,
              maxWidth: 'calc(100vw - 32px)',
              borderRadius: 2,
              border: 1,
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              '&&': {
                overflow: 'hidden',
                maxHeight: previewPanelMaxHeight,
              },
            },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2">Notificações</Typography>
          <Button
            size="small"
            disabled={!unreadCount}
            onClick={() => markAllNotificacoesRead()}
          >
            Ler todas
          </Button>
        </Box>
        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            maxHeight: previewListMaxHeight,
          }}
        >
          {empty && (
            <Box sx={{ px: 2, py: 2 }}>
              <Typography
                variant="body2"
                color="text.secondary"
              >
                Nenhuma notificação.
              </Typography>
            </Box>
          )}
          <Box
            component="ul"
            sx={{ m: 0, p: 0, listStyle: 'none' }}
          >
            {(preview || []).map((item) => (
              <MenuItem
                key={item.id}
                onClick={() => handleOpenItem(item)}
                sx={{
                  alignItems: 'flex-start',
                  py: 1.25,
                  whiteSpace: 'normal',
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  '&:first-of-type': {
                    borderTop: 'none',
                  },
                }}
              >
                <NotificacaoItem
                  item={item}
                  showMessage
                  downloading={(() => {
                    const job = getRelatorioJobFromNotificacao(item);
                    return Boolean(job && downloadingId != null && String(downloadingId) === String(job.id));
                  })()}
                  onDownload={(job) => handleDownload(item, job)}
                />
              </MenuItem>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            bgcolor: 'background.paper',
          }}
        >
          <Divider />
          <Box sx={{ py: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
            <Button
              size="small"
              onClick={() => {
                handleClose();
                history.push('/v2/notificacoes');
              }}
            >
              Ver todas
            </Button>
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                handleClose();
                history.push('/v2/downloads');
              }}
            >
              Arquivos gerados
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

NotificacaoBell.propTypes = {
  unreadCount: PropTypes.number,
  preview: PropTypes.array,
  downloadingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fetchUnreadCount: PropTypes.func.isRequired,
  fetchNotificacaoPreview: PropTypes.func.isRequired,
  markNotificacaoRead: PropTypes.func.isRequired,
  markAllNotificacoesRead: PropTypes.func.isRequired,
  downloadRelatorioJob: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
};

NotificacaoBell.defaultProps = {
  unreadCount: 0,
  preview: [],
  downloadingId: null,
};

const mapStateToProps = (state) => ({
  unreadCount: selectorsNotificacao.getUnreadCount(state),
  preview: selectorsNotificacao.getNotificacaoPreview(state),
  downloadingId: selectorsRelatorioJob.getRelatorioJobDownloadingId(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...actionsNotificacao,
  downloadRelatorioJob: actionsRelatorioJob.downloadRelatorioJob,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(NotificacaoBell));
